import { useQuery } from "@tanstack/react-query";
import { keycloakApi, type KeycloakUser } from "@/integrations/keycloak/client";
import type { UserFilters, UserWithPermissions } from "@/types/user";

/**
 * Mapeia um usuário da API Keycloak para o formato UserWithPermissions.
 * O nome do gerador não é exibido na tabela e a edição é feita na página
 * /gestao-usuario (que busca o usuário por conta própria), então `generators`
 * não é mais resolvido aqui.
 */
const mapKeycloakUserToUserWithPermissions = (
  keycloakUser: KeycloakUser,
): UserWithPermissions => {
  return {
    user_id: keycloakUser.id.toString(),
    auth_id: keycloakUser.keycloak_id || "",
    user_name: keycloakUser.fullname,
    email: keycloakUser.email,
    permission_role: keycloakUser.role,
    is_internal: false, // TODO: Verificar se a API retorna essa informação
    phone_number: keycloakUser.phone,
    created_at: keycloakUser.created_at,
    generator_links: keycloakUser.generator_links,
    generators: null,
    establishments: [],
  };
};

export const useUsersWithKeycloak = (
  page: number = 1,
  pageSize: number = 7,
  filters?: UserFilters,
) => {
  return useQuery({
    queryKey: ["users-with-keycloak", page, pageSize, filters],
    queryFn: async () => {
      const response = await keycloakApi.fetchUsers({
        page,
        pageSize,
        nameFilter: filters?.nameFilter,
        emailFilter: filters?.emailFilter,
        generatorFilter: filters?.generatorFilter,
      });

      const users: UserWithPermissions[] = response.results.map((u) =>
        mapKeycloakUserToUserWithPermissions(u),
      );

      return {
        users,
        totalCount: response.pagination.count,
      };
    },
    retry: (failureCount, error: any) => {
      // Não retentar em caso de erro 401 (não autorizado) ou 403 (proibido)
      if (error?.message?.includes("401") || error?.message?.includes("403")) {
        console.log("Erro de autenticação, não retentando");
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 0,
    refetchOnMount: "always",
  });
};
